import { AccountBalance } from '@mui/icons-material';
import { Box, Snackbar } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AccordionSection from '../components/AccordionSection';
import ConfirmationModal from '../components/ConfirmationModal';
import ActivityLogsSection from '../components/activityLogs/ActivityLogsSection';
import AttachmentsSection from '../components/attachments/AttachmentsSection';
import FieldsSection from '../components/common/FieldsSection/FieldsSection';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import MoreOptions from '../components/common/MoreOptions/MoreOptions';
import TagsSection from '../components/common/TagsSection/TagsSection';
import EventsTimeline from '../components/common/eventTimeline/EventsTimeline';
import ContactBasicInfoForm from '../components/contacts/ContactBasicInfoForm';
import NotesSection from '../components/notes/NotesSection';
import { DisableContext } from '../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../routes/Routes';
import { useGetAccountById } from '../services/react-query/accounts';
import { useCreateContact, useGetContactById, useUpdateContact } from '../services/react-query/contacts';
import { useArchiveContact } from '../services/react-query/contacts/useArchiveContact';
import { useDestroyContact } from '../services/react-query/contacts/useDestroyContact';
import { itemActivitySectionStyling, itemContainerStyling, itemInnerLayoutStyling, itemWrapperStyling } from '../styles/sxStyling';
import { ActivityLogOwnerType } from '../types/ActivityLog';
import { AttachmentOwnerType } from '../types/Attachment';
import { Contact } from '../types/Contact';
import { NoteableType } from '../types/Note';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import useStateWithDirty from '../hooks/useStateWithDirty';

const ContactItem: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const templateUuid = searchParams.get('template') || undefined;

  const { state: contact, setState: setContact, resetState: resetContact, isDirty } = useStateWithDirty<Contact | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const [deletionConfirmationOpen, setDeletionConfirmationOpen] = useState<boolean>(false);

  const { data: contactData, isLoading: isGetContactByIdLoading } = useGetContactById({ uuid: _.toString(uuid), template: templateUuid });

  const accountUuidFromParams = searchParams.get('account') || undefined;
  const accountUuidFromContact = contact?.account_uuid;

  const { data: accountDataFromContact } = useGetAccountById({
    uuid: _.toString(accountUuidFromParams || accountUuidFromContact),
    enabled: !!accountUuidFromParams || !!accountUuidFromContact,
  });

  const saveCallback = () => setSaved(true);
  const { mutate: createContact, isPending: isCreateContactMutation } = useCreateContact({
    onSuccessCallback: (created: Contact) => {
      saveCallback();
      navigate(`/${ROUTE_PATHS.CONTACTS}/${created.uuid}`);
    },
  });
  const { mutate: updateContact, isPending: isUpdateContactMutation } = useUpdateContact({ uuid: _.toString(uuid), onSuccessCallback: saveCallback });

  const returnToList = () => navigate(`/${ROUTE_PATHS.CONTACTS}`);
  const { mutate: destroyContact } = useDestroyContact({ uuid: _.toString(uuid), onSuccessCallback: returnToList });
  const { mutate: archiveContact } = useArchiveContact({ uuid: _.toString(uuid), onSuccessCallback: returnToList });

  useEffect(() => {
    if (contactData) {
      resetContact({
        ...contactData,
        account_uuid: contactData.account_uuid || accountUuidFromParams || '',
      });
    }
  }, [contactData, accountUuidFromParams]);

  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable('archived', !!contact?.discarded_at);

  const isNew = uuid === 'new';

  if (!contact || isGetContactByIdLoading) return <Loading />;

  const handleSave = () => {
    if (!contact) return;

    if (isNew) createContact(contact);
    else updateContact(contact);
  };

  const fullName = [contactData?.first_name, contactData?.last_name].filter(Boolean).join(' ');
  const email = contactData?.email ? ` (${contactData.email})` : '';
  const combinedHeader = `${fullName} ${email}`.replace(/\s+/g, ' ').trim();

  return (
    <Box sx={itemWrapperStyling}>
      <ItemHeader
        title={isNew ? 'Create Contact' : combinedHeader}
        onSave={handleSave}
        isLoading={Boolean(isCreateContactMutation || isUpdateContactMutation)}
        disableActions={!isDirty}
        archived={!!contact.discarded_at}
        showMoreOptions={!isNew ? <MoreOptions onDelete={destroyContact} onArchive={archiveContact} name={contact?.full_name} itemName="contact" /> : null}
        module={UserConfigurableModule.CONTACTS}
        infoItems={[
          { content: contactData?.contact_title_value ?? '—' },
          {
            icon: <AccountBalance fontSize="small" color="action" />,
            content: contactData?.account_name ?? '—',
          },
        ]}
      />

      {/* Left Side */}
      <Box sx={itemInnerLayoutStyling}>
        <Box sx={itemContainerStyling}>
          <AccordionSection title="Contact Information" collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_INFORMATION}>
            <ContactBasicInfoForm contact={contact} setContact={setContact} account={accountDataFromContact} disabled={disabled} />
          </AccordionSection>

          <FieldsSection
            fields={contact.fields}
            setFields={(fields) => setContact({ ...contact, fields })}
            collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_CUSTOM_FIELDS}
          />

          {!isNew && <EventsTimeline entityUuid={contact.uuid} entityType={'contacts'} />}

          <TagsSection tags={contact.tags} collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_TAGS} setTags={(tags) => setContact({ ...contact, tags })} />

          <AttachmentsSection collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_ATTACHMENTS} ownerType={AttachmentOwnerType.CONTACT} ownerUuid={uuid ?? ''} />

          <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
          <ConfirmationModal
            isOpen={deletionConfirmationOpen}
            onCancel={() => setDeletionConfirmationOpen(false)}
            onConfirm={destroyContact}
            text={`Are you sure you want to delete the contact ${contact.full_name}? This operation is irreversible!`}
          />
        </Box>

        {/* Right Side */}
        {!isNew && (
          <Box sx={itemActivitySectionStyling}>
            <ActivityLogsSection ownerUuid={uuid} ownerType={ActivityLogOwnerType.CONTACT} collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_ACTIVITY_LOG} />
            <NotesSection
              noteable={{
                noteableUuid: uuid ?? '',
                noteableType: NoteableType.CONTACT,
              }}
              collapseId={UserSettingsCollapseKeys.CONTACT_ITEM_NOTES}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContactItem;
